import { Composer } from "@crew/ui/components/composer";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useMe } from "../../app/me";
import { useAskGate } from "../setup/api";
import { AskNotes } from "../setup/ask-gate";
import { gateNotice } from "../setup/ask-notice";
import { ASK_TEXT, type Attach } from "../docs/attach";
import { ComposerDocChip } from "../docs/parts";
import { startThread } from "./api";
import type { Uploaded } from "../../app/api";
import { editedBody, filesProps, useComposerFiles, wordlessBody } from "../files/composer-files";
import { startDrafts, useStartDraft, type StartSurface } from "./starts";

/**
 * What the composer starts with, and its key: an Edited start's words (each
 * draft remounts it once, then they're taken), else Ask to change's. The
 * doc: Ask to change's, else the Edited start's (Chats has no Ask to change).
 */
function useStartText(
  surface: StartSurface | undefined,
  attach: Attach | undefined,
  putFiles: (files: Uploaded[]) => void,
) {
  const draft = useStartDraft(surface);
  const attached = attach?.docId ?? null;
  useEffect(() => {
    if (!surface || draft.body === null) return;
    // Its files back as chips, with its words (Codex on #435).
    if (draft.files.length) putFiles(draft.files);
    startDrafts.taken(surface);
  }, [surface, draft, putFiles]);
  const dropDoc = () => (attached ? attach?.clear() : surface && startDrafts.dropDoc(surface));
  return {
    key: `${attach?.key ?? ""}:${draft.n}`,
    initialText:
      draft.body === null ? (attached ? ASK_TEXT : "") : editedBody(draft.body, draft.files),
    autoFocus: Boolean(attached) || draft.body !== null,
    docId: attached ?? draft.doc_id,
    dropDoc,
  };
}

/**
 * An ask that starts a thread for the Chief of Staff (`POST /api/threads`):
 * the panel (F7), Chats (F6) and an Area page. Send makes the thread's id and
 * the message's here and opens the thread at once, its message "Sending…"
 * from the outbox (`starts.ts`); a start that isn't sent says so there, with
 * Retry and Edit. Edit brings its words back here, on `surface`. Disabled
 * until the AI is connected (slice 1b spec §4.5). In the panel, Ask to
 * change's doc rides along as a chip (slice 2d spec §10.5) on the start.
 */
export function StartComposer({
  placeholder,
  label,
  size,
  attach,
  surface,
  areaId,
  onStarted,
}: {
  placeholder: string;
  label?: string;
  size?: "lg" | "md";
  /** The doc Ask to change attached, if any. */
  attach?: Attach;
  /** Where an Edited start's words come back to. */
  surface?: StartSurface;
  /** Slice 6d (spec §8.2): an Area page's ask starts a chat in the Area. */
  areaId?: string;
  onStarted: (threadId: string) => unknown;
}) {
  const client = useQueryClient();
  const user = useMe().data?.user;
  const gate = useAskGate();
  // Slice 5b (spec §6.2): files attached here start the chat with it.
  const files = useComposerFiles();
  const text = useStartText(surface, attach, files.put);
  const { docId } = text;
  return (
    <div className="flex flex-col gap-2">
      <Composer
        key={text.key}
        clearOnSubmit
        disabled={!gate.open || !user}
        notice={gateNotice(gate)}
        size={size}
        placeholder={placeholder}
        label={label}
        initialText={text.initialText}
        autoFocus={text.autoFocus}
        {...filesProps(
          files,
          docId ? <ComposerDocChip docId={docId} onRemove={text.dropDoc} /> : undefined,
          gate.open && Boolean(user),
        )}
        onSubmit={(body) => {
          if (!user) return;
          if (surface) startDrafts.dropDoc(surface);
          const sent = files.ready;
          const ask = { body: body || wordlessBody(sent), doc_id: docId, area_id: areaId };
          onStarted(startThread(client, user.id, sent.length ? { ...ask, files: sent } : ask));
          files.clear();
        }}
      />
      {/* A start that isn't sent says so on its message, in the thread; here only the gate. */}
      <AskNotes gate={gate} error={null} />
    </div>
  );
}
